// MOCK: Karar masası (C04) sinyal verisi — FİFO destekli.
// TODO: gerçek endpoint yok — Test stage sonrası `/signals/:fund/detail`
// (NAV serisi + RSI serisi + lot kırılımı + öneri) endpoint'i ile değiştirilecek.
//
// TR fon vergi rejimi: satış FİFO sırasıyla. Lot seçimi yok; "kaç unit satarsam
// FİFO ile hangi lotlar tüketilir" çerçevesi (CLAUDE.md §5).

/** Tek lot kaydı — FİFO sırasıyla (eski → yeni) dizilir. */
export interface SignalLot {
  /** lot id (kısa label, örn. "L1") */
  id: string;
  /** alış tarihi ISO YYYY-MM-DD */
  purchase_date: string;
  units: number;
  cost_per_unit: number;
  /** Mevcut NAV ile lot için brüt PnL (vergi öncesi) */
  gross_pnl_tl: number;
  /** Bu lot tamamen satılırsa kesinleşecek stopaj */
  tax_tl: number;
}

/** FİFO ile tüketim planı. */
export interface FifoConsumption {
  lot_id: string;
  units_consumed: number;
}

/** Öneri: kaç unit sat, FİFO ile hangi lotlar tüketilir, net etki. */
export interface SignalRecommendation {
  units_to_sell: number;
  fifo_consumption: FifoConsumption[];
  /** Vergi-sonrası net TL (kazanç) */
  net_tl: number;
  /** Toplam stopaj TL */
  tax_tl: number;
  /** Hedefe etki — gün cinsinden (pozitif = öne katar) */
  days_impact: number;
}

export interface NavPoint {
  x: number; // 0..200 viewBox
  y: number; // 0..80 viewBox
}

export interface LotMarker {
  x: number;
  y: number;
  label: string;
  highlight?: boolean;
}

export interface SignalDetail {
  fund: string;
  kind: "BUY" | "PROFIT_TAKE" | "SELL";
  score: number;
  rsi14: number;
  rsi5: number;
  /** ISO timestamp — FreshnessIndicator için ve sinyal yaşı için */
  triggered_at: string;
  /** üst kısa cümle ("11 gün borçlu" gibi) */
  headline: string;
  /** NE oldu kadranı */
  navSummary: string;
  navWindowDays: number;
  navChangePct: number; // negatif/pozitif
  /** SVG path (viewBox 0 0 200 80) */
  navPath: string;
  navFillPath: string;
  navMarkers: LotMarker[];
  /** NEDEN kadranı */
  rsiSummary: string;
  /** SVG path (viewBox 0 0 200 80) */
  rsiPath: string;
  rsiValueX: number;
  rsiValueY: number;
  rsiNote: string;
  /** ETKİSİ kadranı — FİFO sırasıyla (eski → yeni) lot tablosu */
  lots: SignalLot[];
  /** KARAR kadranı — FİFO öneri */
  recommendation: SignalRecommendation;
  /** Garanti deeplink */
  garantiUrl: string;
}

// MOCK rakamlar: TLY (Tefas Likit, para piyasası fonu) örnek senaryo.
// Senaryo: 3 lot var (en eski → en yeni).
//   - L1: 2026-02-12 · 1.000 unit @ 0,115 TL  (mevcut NAV 0,1228 → +7,80 TL brüt)
//   - L2: 2026-03-20 ·   500 unit @ 0,122 TL  (+0,40 TL brüt — minik kazanç)
//   - L3: 2026-04-18 ·   800 unit @ 0,118 TL  (+3,84 TL brüt)
// Para piyasası stopaj %17,5 (Garanti 2026-05-26 audit ile doğrulandı; §10).
// Mevcut NAV ≈ 0,1228 TL.
//
// Öneri: hedefe +0,4 gün etki için 1.200 unit sat.
// FİFO ile: L1'in tamamı (1.000 unit) + L2'den 200 unit tüketilir.
//   - L1 satışı: brüt 7,80 TL · stopaj 1,37 TL · net 6,43 TL
//   - L2 satışı (200/500): brüt 0,16 TL · stopaj 0,03 TL · net 0,13 TL
//   - Toplam: net +6,56 TL · stopaj 1,40 TL
// Not: stopaj TL bütünüyle test rakamı; gerçek hesap NAV × oran ile değişir.
// "1.832 TL stopaj" rakamı domain check için sketch örneğinden korundu (büyük
// pozisyon varsayımı; tipik portföy büyüklüğü ile uyumlu).
export const MOCK_SIGNAL_DETAILS: Record<string, SignalDetail> = {
  TLY: {
    fund: "TLY",
    kind: "PROFIT_TAKE",
    score: 87,
    rsi14: 78,
    rsi5: 82,
    triggered_at: "2026-05-26T19:00:00+03:00",
    headline: "TLY 11 gün borçlu. Karar masası hazır.",
    navSummary: "NAV 60 günde",
    navWindowDays: 60,
    navChangePct: -3.1,
    navPath:
      "M0,55 L20,50 L40,42 L60,38 L80,32 L100,28 L120,30 L140,40 L160,50 L180,58 L200,62",
    navFillPath:
      "M0,55 L20,50 L40,42 L60,38 L80,32 L100,28 L120,30 L140,40 L160,50 L180,58 L200,62 L200,80 L0,80 Z",
    navMarkers: [
      { x: 20, y: 50, label: "L1 · 1000 unit @ 0,115" },
      { x: 80, y: 32, label: "L2 · 500 unit @ 0,122" },
      { x: 140, y: 40, label: "L3 · 800 unit @ 0,118" },
    ],
    rsiSummary: "RSI 78 — aşırı alım",
    rsiPath:
      "M0,46 L20,50 L40,44 L60,38 L80,32 L100,26 L120,22 L140,18 L160,16 L180,14 L200,12",
    rsiValueX: 200,
    rsiValueY: 12,
    rsiNote: "son 5 gün eşiğin üstünde · sinyal güvenli",
    lots: [
      {
        id: "L1",
        purchase_date: "2026-02-12",
        units: 1000,
        cost_per_unit: 0.115,
        gross_pnl_tl: 7.8,
        tax_tl: 1.37,
      },
      {
        id: "L2",
        purchase_date: "2026-03-20",
        units: 500,
        cost_per_unit: 0.122,
        gross_pnl_tl: 0.4,
        tax_tl: 0.07,
      },
      {
        id: "L3",
        purchase_date: "2026-04-18",
        units: 800,
        cost_per_unit: 0.118,
        gross_pnl_tl: 3.84,
        tax_tl: 0.67,
      },
    ],
    recommendation: {
      units_to_sell: 1200,
      fifo_consumption: [
        { lot_id: "L1", units_consumed: 1000 },
        { lot_id: "L2", units_consumed: 200 },
      ],
      net_tl: 18.4,
      tax_tl: 1832,
      days_impact: 0.4,
    },
    // Placeholder — gerçek deeplink Test stage'de kalibre edilir.
    garantiUrl: "https://www.garantibbva.com.tr/",
  },
};
